import React, { useState } from 'react';
import { func, object, string, bool } from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { CUSTOM_BOX, DEFAULT_ADDRESS, LISTING_ADDRESS, LISTING_STATE_DRAFT, PRE_DEFINED_BOX } from '../../../../util/types';
// Import shared components
import { H3 } from '../../../../components';

// Import modules from this directory
import EditListingDeliveryForm from './EditListingDeliveryForm';
import css from './EditListingDeliveryPanel.module.css';


const EditListingDeliveryPanel = props => {
  // State is needed since LocationAutocompleteInput doesn't have internal state
  // and therefore re-rendering would overwrite the values during XHR call.
  const { boxSizes, packageWeight, packagingType, packagePreDefinedWeight, listingLocation, addressType: savedAddressType } =
    props.listing.attributes.publicData || {};

  const [saveAsDraft, setSaveAsDraft] = useState(null);
  const [addressType, setAddressType] = useState(savedAddressType || DEFAULT_ADDRESS);

  const {
    className,
    rootClassName,
    listing,
    listingTypes,
    marketplaceCurrency,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    currentUser,
    handlePublishListing,
    history,
    onAddressValidationWithShippo,
    validateAddressInProgress
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const isPublished = listing?.id && listing?.attributes.state !== LISTING_STATE_DRAFT;
  const priceCurrencyValid = listing?.attributes?.price?.currency === marketplaceCurrency;
  const listingType = listing?.attributes?.publicData?.listingType;
  const listingTypeConfig = listingTypes.find(conf => conf.listingType === listingType);
  const hasStockInUse = listingTypeConfig.stockType === 'multipleItems';


  const { height, length, width, weight } = boxSizes || {};
  const packageLength = length;
  const packageWidth = width;
  const packageHeight = height;

  const { email, firstName } = currentUser?.attributes?.profile?.publicData || {};
  const { ...rest } = listingLocation || {};
  const initialValues = {
    ...rest,
    boxSizes,
    packageWeight: packageWeight || weight,
    packagingType,
    packageLength,
    packageHeight,
    packageWidth,
    packagePreDefinedWeight,
  };

  if (listingLocation && listingLocation.address) {
    const { address } = listingLocation;
    const geolocation = listing?.attributes?.geolocation || {};
    Object.assign(initialValues, {
      location: {
        search: address,
        selectedPlace: { address, origin: geolocation },
      },
    });
  }


  return (
    <div className={classes}>
      <div className={css.container}>
        <H3 as="h1">
          <FormattedMessage
            id="EditListingDeliveryPanel.createListingTitle"
            values={{ lineBreak: <br /> }}
          />
          {/* )} */}
        </H3>
        {priceCurrencyValid ? (
          <EditListingDeliveryForm
            className={css.form}
            initialValues={initialValues}
            listing={listing}
            onSubmit={values => {
              const {
                boxSizes,
                packageWeight,
                packagingType,
                packagePreDefinedWeight,
                packageLength,
                packageHeight,
                packageWidth,
                location,
                postalCode,
                state,
                apartment,
                city,
                poBoxNumber,
                addressLine1,
                addressLine2,
              } = values;

              const { address, origin } = location?.selectedPlace || {};
              const { search } = location || {};

              const updateValues = {
                geolocation: origin,
                publicData: {
                  boxSizes:
                    packagingType === CUSTOM_BOX
                      ? {
                        length: packageLength,
                        height: packageHeight,
                        width: packageWidth,
                        weight: packageWeight,
                      }
                      : { ...boxSizes, weight: packagePreDefinedWeight },
                  packagingType,
                  packagePreDefinedWeight: packagingType === PRE_DEFINED_BOX ? packagePreDefinedWeight : null,
                  shippingEnabled: true,
                  updatedAt: Math.floor(Date.now() / 1000),
                  poBoxNumber,
                  listingLocation: (addressType == LISTING_ADDRESS) ? {
                    address: address || search,
                    postalCode,
                    state,
                    apartment,
                    city,
                    country: 'US',
                    poBoxNumber,
                  } : null,
                  addressType
                },
              };
              if(addressType == LISTING_ADDRESS) {
                onAddressValidationWithShippo({
                  postalCode,
                  state,
                  city,
                  addressLine1,
                  addressLine2: addressLine2 || apartment,
                  email,
                  firstName,
                }).then(() => {
                  onSubmit(updateValues);
                });
              } else {
                onSubmit(updateValues);
              }
            }}
            marketplaceCurrency={marketplaceCurrency}
            hasStockInUse={hasStockInUse}
            saveActionMsg={submitButtonText}
            disabled={disabled}
            ready={ready}
            updated={panelUpdated}
            updateInProgress={updateInProgress}
            fetchErrors={errors}
            autoFocus
            currentUser={currentUser}
            onSaveDraft={onSubmit}
            handlePublishListing={handlePublishListing}
            history={history}
            setSaveAsDraft={setSaveAsDraft}
            saveAsDraft={saveAsDraft}
            onAddressValidationWithShippo={onAddressValidationWithShippo}
            setAddressType={setAddressType}
            addressType={addressType}
            validateAddressInProgress={validateAddressInProgress}
          />
        ) : (
          <div className={css.priceCurrencyInvalid}>
            <FormattedMessage id="EditListingPricingPanel.listingPriceCurrencyInvalid" />
          </div>
        )}
      </div>
    </div>
  );
};

EditListingDeliveryPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingDeliveryPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,
  marketplaceCurrency: string.isRequired,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingDeliveryPanel;
